import {
  ChangeDetectorRef,
  Directive,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewContainerRef,
  inject,
} from '@angular/core';
import { BehaviorSubject, isObservable, Observable, of, Subject } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';

import { SpinnerComponent } from '@plugins/client/spinners';

const isDefined = <T>(val: T | undefined): val is T => {
  return val !== undefined;
};

class LetContext<T> {
  public pluginsLet!: T;
}

@Directive({
  selector: '[pluginsLet]',
  standalone: false,
})
export class LetDirective<T> implements OnInit, OnDestroy {
  @Input('pluginsLet') set values(val: Observable<T> | T) {
    this._values$.next(val);
  }
  @Input('pluginsLetLoading') set loading(val: Observable<boolean> | boolean) {
    this._loading$.next(val);
  }
  @Input('pluginsLetWithLoading') withLoading = false;

  private _values$ = new BehaviorSubject<Observable<T> | T | undefined>(
    undefined,
  );
  private values$ = this._values$.asObservable().pipe(
    filter(isDefined),
    distinctUntilChanged(),
    switchMap((val) => (isObservable(val) ? val : of(val))),
  );

  private _loading$ = new BehaviorSubject<Observable<boolean> | boolean>(false);
  private loading$ = this._loading$.asObservable().pipe(
    distinctUntilChanged(),
    switchMap((val) => (isObservable(val) ? val : of(val))),
  );

  private context = new LetContext<T>();
  private destroy$ = new Subject<void>();

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly viewContainer = inject(ViewContainerRef);
  private readonly templateRef = inject(TemplateRef<LetContext<T>>);

  ngOnInit(): void {
    if (this.withLoading) {
      this.showLoading();
    }
    this.values$
      .pipe(
        distinctUntilChanged(),
        tap((val) => {
          this.context.pluginsLet = val;
          this.cdr.markForCheck();
        }),
        switchMap(() => this.loading$),
        distinctUntilChanged(),
        // filter first emit if we are using withLoading and it's loading
        filter((loading, i) => !this.withLoading || !(loading && !i)),
        tap((loading) => (loading ? this.showLoading() : this.hideLoading())),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  private showLoading(): void {
    this.viewContainer.clear();
    this.viewContainer.createComponent(SpinnerComponent);
  }

  private hideLoading() {
    this.viewContainer.clear();
    this.viewContainer.createEmbeddedView(this.templateRef, this.context);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  static ngTemplateContextGuard<T>(
    dir: LetDirective<T>,
    context: unknown,
  ): context is LetContext<T> {
    return true;
  }
}
